import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-paper/90 backdrop-blur-md border-b border-line" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between h-16 sm:h-20">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo-link">
          <span className="w-9 h-9 rounded-full bg-ink text-paper flex items-center justify-center group-hover:bg-terra transition-colors duration-300">
            <Leaf className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <span className="font-serif font-semibold text-ink text-base sm:text-lg tracking-tight leading-none">
            Jen Cullerton Johnson
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `link-underline font-mono text-[11px] uppercase tracking-[0.18em] py-1 transition-colors ${
                  isActive ? "text-terra" : "text-ink/80 hover:text-ink"
                }`
              }
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
            >
              {l.name}
            </NavLink>
          ))}
          <Link
            to="/collaborate"
            className="group inline-flex items-center gap-2 bg-terra hover:bg-terradark text-paper rounded-full pl-5 pr-4 py-2.5 font-medium text-sm transition-colors duration-300"
            data-testid="nav-book-visit-btn"
          >
            Book a Visit
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center text-ink"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden absolute top-full inset-x-0 bg-paper border-b border-line shadow-xl"
            data-testid="nav-mobile-menu"
          >
            <nav className="px-6 py-6 flex flex-col gap-1" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.35 }}
                >
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `block font-serif text-3xl py-3 border-b border-line ${
                        isActive ? "text-terra italic" : "text-ink"
                      }`
                    }
                    data-testid={`nav-mobile-link-${l.to.replace("/", "") || "home"}`}
                  >
                    {l.name}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/collaborate"
                className="mt-5 inline-flex items-center justify-center gap-2 bg-terra text-paper rounded-full px-6 py-3.5 font-medium"
                data-testid="nav-mobile-book-btn"
              >
                Book a Visit <ArrowUpRight className="w-4 h-4" />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
